import { PHASES } from "@/data/program";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Curriculum() {
  return (
    <section
      id="curriculum"
      data-testid="curriculum-section"
      className="bg-[#FAFAF7] relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Sticky sidebar */}
          <aside className="md:col-span-4 lg:col-span-3">
            <div className="md:sticky md:top-32">
              <p className="font-mono uppercase tracking-[0.24em] text-xs text-slate-500 mb-6">
                — The Curriculum
              </p>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight text-slate-900">
                Six phases.<br />
                <span className="italic text-slate-500">One deployed portfolio.</span>
              </h2>
              <p className="mt-6 text-slate-600 font-sans leading-relaxed text-sm">
                Each phase concludes with a flagship industry project — shipped
                to production on GCP or Azure, with monitoring, CI/CD and a live URL.
              </p>
              <div className="mt-10 hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d"
                  alt=""
                  className="w-full h-64 object-cover grayscale opacity-70"
                />
              </div>
            </div>
          </aside>

          {/* Phase accordion */}
          <div className="md:col-span-8 lg:col-span-9">
            <Accordion type="single" collapsible defaultValue="phase-1" className="space-y-0">
              {PHASES.map((phase, i) => (
                <motion.div
                  key={phase.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                >
                  <AccordionItem
                    value={phase.id}
                    className="border-b border-slate-300 border-t-0"
                    data-testid={`curriculum-phase-${phase.number}`}
                  >
                    <AccordionTrigger
                      className="hover:no-underline py-8"
                      data-testid={`curriculum-phase-trigger-${phase.number}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-baseline md:gap-8 text-left w-full pr-4">
                        <span className="font-mono text-xs uppercase tracking-[0.24em] text-slate-500 md:w-16 shrink-0">
                          {phase.number}
                        </span>
                        <span className="font-serif text-2xl md:text-3xl text-slate-900 tracking-tight flex-1">
                          {phase.title}
                        </span>
                        <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mt-2 md:mt-0">
                          {phase.weeks}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-10 pt-2">
                      <div className="md:pl-24 grid grid-cols-1 md:grid-cols-5 gap-8">
                        <div className="md:col-span-3">
                          <p className="font-serif italic text-lg text-slate-700 mb-6">
                            {phase.focus}
                          </p>
                          <ul className="space-y-3">
                            {phase.modules.map((m, idx) => (
                              <li key={idx} className="flex gap-3 text-slate-700 font-sans text-sm leading-relaxed">
                                <span className="font-mono text-xs text-slate-400 mt-1">
                                  {String(idx + 1).padStart(2, "0")}
                                </span>
                                <span>{m}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="md:col-span-2 bg-slate-900 text-slate-50 p-8">
                          <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-400 mb-3">
                            Flagship project · {phase.project.industry}
                          </p>
                          <h4 className="font-serif text-xl leading-snug text-slate-50 mb-4">
                            {phase.project.name}
                          </h4>
                          <p className="text-xs font-mono text-slate-400 leading-relaxed">
                            {phase.project.stack}
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
